// Reads the first-touch attribution cookie written by the marketing site
// (studygenie.io) on the shared .studygenie.io domain. Keep the cookie name
// and shape in sync with studygenie-site-new/src/lib/attribution.ts.
export const ATTRIBUTION_COOKIE = "sg_attr";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

type UtmKey = (typeof UTM_KEYS)[number];

// Cookie values flow into request payloads and PostHog properties. Cap them
// here so an oversized referrer can never fail the request carrying it.
const MAX_VALUE_LENGTH = 2048;

export type Attribution = {
  landing_page: string;
  referrer: string;
  ts: string;
} & Partial<Record<UtmKey, string>>;

export function readAttribution(cookieString: string): Attribution | null {
  const entry = cookieString
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE}=`));
  if (!entry) return null;
  try {
    const parsed = JSON.parse(
      decodeURIComponent(entry.slice(ATTRIBUTION_COOKIE.length + 1)),
    );
    if (typeof parsed?.landing_page !== "string") return null;
    const capped: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "string")
        capped[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
    return capped as Attribution;
  } catch {
    return null;
  }
}

export function attributionProperties(
  attr: Attribution | null,
): Record<string, string> {
  if (!attr) return {};
  const props: Record<string, string> = {
    landing_page: attr.landing_page,
  };
  if (attr.referrer) props.first_referrer = attr.referrer;
  for (const key of UTM_KEYS) {
    const value = attr[key];
    if (value) props[key] = value;
  }
  return props;
}
